import { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, Plus, Trash2, CircleAlert, Layers, EllipsisVertical, SquarePen } from 'lucide-react';
import type { Engagement } from '../types';
import {
  getSubGroupings,
  createSubGrouping,
  deleteSubGrouping,
  renameSubGrouping,
  countAccountsWithSubGrouping,
  validateNoDuplicateSubGrouping,
} from '../db/database';
import type { SubGrouping } from '../db/database';

// ============================================================================
// PROPS
// ============================================================================

interface SubGroupingManagerProps {
  engagement: Engagement;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function SubGroupingManager({ engagement }: SubGroupingManagerProps) {
  // Data state
  const [subGroupings, setSubGroupings] = useState<SubGrouping[]>([]);

  // Search / filter (now doubles as the create input)
  const [searchQuery, setSearchQuery] = useState('');

  // Create feedback
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<SubGrouping | null>(null);
  const [deleteAffectedCount, setDeleteAffectedCount] = useState(0);

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Kebab menu
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  // ============================================================================
  // CLICK-OUTSIDE: close kebab menu
  // ============================================================================

  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openMenuId]);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  const loadSubGroupings = useCallback(async () => {
    if (!engagement.id) return;
    setIsLoading(true);
    try {
      const data = await getSubGroupings(engagement.id);
      setSubGroupings(data);
    } catch (err) {
      console.error('Failed to load SubGroupings:', err);
    } finally {
      setIsLoading(false);
    }
  }, [engagement.id]);

  useEffect(() => {
    loadSubGroupings();
  }, [loadSubGroupings]);

  // ============================================================================
  // FILTERED LIST (useMemo for performance)
  // ============================================================================

  const filteredSubGroupings = useMemo(() => {
    if (!searchQuery.trim()) return subGroupings;
    const q = searchQuery.toLowerCase();
    return subGroupings.filter((sg) =>
      sg.name.toLowerCase().includes(q)
    );
  }, [subGroupings, searchQuery]);

  // ============================================================================
  // SEARCH-TO-CREATE: Derived state for the green Plus icon
  // ============================================================================

  const canCreate = useMemo(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return false;
    // Hide the Plus icon if an exact match (case-insensitive) already exists
    return !subGroupings.some(
      (sg) => sg.name.toLowerCase() === trimmed.toLowerCase()
    );
  }, [searchQuery, subGroupings]);

  // ============================================================================
  // CREATE HANDLER (now driven by searchQuery)
  // ============================================================================

  const handleCreate = async () => {
    const trimmed = searchQuery.trim();
    if (!trimmed || !engagement.id) return;

    setIsCreating(true);
    setCreateError('');

    try {
      // Uniqueness check (belt-and-suspenders with the DB)
      const isUnique = await validateNoDuplicateSubGrouping(engagement.id, trimmed);
      if (!isUnique) {
        setCreateError(`"${trimmed}" already exists in this engagement.`);
        setIsCreating(false);
        return;
      }

      await createSubGrouping(engagement.id, trimmed);
      setSearchQuery('');
      setCreateError('');
      await loadSubGroupings();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create SubGrouping.');
    } finally {
      setIsCreating(false);
    }
  };

  // ============================================================================
  // KEYBOARD: Enter to create
  // ============================================================================

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canCreate && !isCreating) {
      e.preventDefault();
      handleCreate();
    }
  };

  // ============================================================================
  // DELETE HANDLER
  // ============================================================================

  const handleDeleteClick = async (sg: SubGrouping) => {
    if (!engagement.id) return;
    const count = await countAccountsWithSubGrouping(engagement.id, sg.name);
    setDeleteAffectedCount(count);
    setDeleteTarget(sg);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await deleteSubGrouping(deleteTarget.id);
      setDeleteTarget(null);
      await loadSubGroupings();
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // ============================================================================
  // RENAME HANDLER
  // ============================================================================

  const handleEditStart = (sg: SubGrouping) => {
    setEditingId(sg.id!);
    setEditValue(sg.name);
    setEditError('');
    setOpenMenuId(null);
  };

  const handleRenameSave = async () => {
    if (!editingId) return;
    const trimmed = editValue.trim();
    if (!trimmed) return;
    setIsSaving(true);
    setEditError('');

    try {
      await renameSubGrouping(editingId, trimmed);
      setEditingId(null);
      await loadSubGroupings();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to rename.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditError('');
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="h-full flex flex-col bg-slate-900 transition-colors duration-300">
      <div className="max-w-4xl mx-auto w-full flex flex-col h-full bg-white dark:bg-slate-900 border-x border-slate-200 dark:border-slate-800 shadow-sm">

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Layers className="w-6 h-6 text-sage-500" />
              SubGrouping Manager
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {engagement.clientName} — {subGroupings.length} SubGrouping{subGroupings.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* ── SMART SEARCH / CREATE BAR ──────────────────────────────────── */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-slate-100 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCreateError('');
              }}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search or create a SubGrouping…"
              className="w-full pl-10 pr-10 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 text-sm placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-sage-500 focus:border-sage-500 transition-all"
            />

            {/* Green Plus icon — visible only when a new, unique name is typed */}
            {canCreate && (
              <button
                onClick={handleCreate}
                disabled={isCreating}
                title={`Create "${searchQuery.trim()}"`}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-sage-500 hover:text-sage-600 hover:bg-sage-50 dark:hover:bg-sage-950/30 disabled:opacity-50 transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Inline create error */}
          {createError && (
            <p className="text-xs text-red-500 dark:text-red-400 mt-1.5 pl-1">{createError}</p>
          )}
        </div>

        {/* ── SUBGROUPINGS LIST ──────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6 space-y-2 custom-scrollbar">
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-slate-500 dark:text-slate-400 text-sm">Loading SubGroupings…</p>
            </div>
          ) : filteredSubGroupings.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="w-12 h-12 text-slate-200 dark:text-slate-800 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                {searchQuery.trim()
                  ? 'No matches found. Press Enter or tap + to create it.'
                  : 'No SubGroupings yet. Type a name above to get started.'}
              </p>
            </div>
          ) : (
            filteredSubGroupings.map((sg) => (
              <div key={sg.id} className="space-y-1">
                <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm hover:border-sage-300 dark:hover:border-sage-800 transition-all">
                  {editingId === sg.id ? (
                    /* ── INLINE EDIT MODE ── */
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        autoFocus
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !isSaving) handleRenameSave();
                          if (e.key === 'Escape') handleEditCancel();
                        }}
                        className="flex-1 px-2 py-1 text-sm rounded border border-sage-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-sage-500"
                      />
                      <button
                        onClick={handleRenameSave}
                        disabled={isSaving || !editValue.trim()}
                        className="px-3 py-1 text-xs font-medium rounded bg-sage-600 hover:bg-sage-700 text-white disabled:opacity-50 transition-colors"
                      >
                        {isSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        onClick={handleEditCancel}
                        disabled={isSaving}
                        className="px-3 py-1 text-xs font-medium rounded text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    /* ── NORMAL VIEW MODE ── */
                    <>
                      <span className="text-sm text-slate-900 dark:text-slate-100 font-medium">
                        {sg.name}
                      </span>

                      {/* Kebab menu */}
                      <div
                        className="relative"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === sg.id ? null : sg.id!);
                          }}
                          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                          <EllipsisVertical className="w-4 h-4" />
                        </button>

                        {openMenuId === sg.id && (
                          <div className="absolute right-0 top-full mt-1 w-36 glass-panel rounded-lg py-1 z-50">
                            <button
                              onClick={() => handleEditStart(sg)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2 text-taxtrail-text dark:text-taxtrail-dark-text"
                            >
                              <SquarePen className="w-4 h-4" />
                              Edit
                            </button>
                            <div className="border-t border-slate-200 dark:border-neutral-700 my-1" />
                            <button
                              onClick={() => {
                                handleDeleteClick(sg);
                                setOpenMenuId(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2 text-taxtrail-red dark:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Inline rename error (shown below the row) */}
                {editingId === sg.id && editError && (
                  <p className="text-xs text-red-500 dark:text-red-400 pl-4 flex items-center gap-1">
                    <CircleAlert className="w-3 h-3" />
                    {editError}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── DELETE CONFIRMATION MODAL (glass-panel backdrop-blur) ────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm p-4">
          <div className="glass-panel max-w-md w-full p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400 mb-4">
              <CircleAlert className="w-6 h-6" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Delete SubGrouping?</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              Deleting{' '}
              <span className="font-bold text-slate-900 dark:text-slate-100">
                &ldquo;{deleteTarget.name}&rdquo;
              </span>{' '}
              will affect{' '}
              <span className="font-bold text-slate-900 dark:text-slate-100">
                {deleteAffectedCount} account{deleteAffectedCount !== 1 ? 's' : ''}
              </span>
              . Those accounts will revert to having no SubGrouping. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isDeleting ? 'Deleting…' : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
